"use client"

// The console's one confirm-dialog shell.
//
// Four near-identical copies of this Base UI alert-dialog geometry had grown across the
// app (the resource-registry action dialog, the integrations bridge confirm, the cash
// review decision confirm, and the unsaved-changes discard prompt). They differed only in
// width, whether they carried a severity icon, and whether their copy was bilingual — so
// the geometry now lives here exactly once and the callers supply content.
//
// House style: flat. A hairline ring for the popup edge, a soft drop shadow for genuine
// elevation, no bevelled or extruded edges anywhere.

import { AlertDialog } from "@base-ui/react/alert-dialog"
import type { ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/** Confirm-button treatment. `destructive` paints the primary action red. */
export type ConfirmDialogTone = "neutral" | "destructive"

/** Popup width. Matches the three widths the console already used. */
export type ConfirmDialogSize = "sm" | "md" | "lg"

const POPUP_CLASS =
  "w-full rounded-xl bg-card p-5 shadow-2xl ring-1 ring-foreground/10 outline-none data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0"

const SIZE_CLASS: Record<ConfirmDialogSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
}

export type ConfirmDialogProps = {
  open: boolean
  /** Fires for every dismissal route Base UI knows about, including Esc and the Cancel button. */
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  /** Small uppercase line above the title (severity banner in the action dialog). */
  eyebrow?: ReactNode
  /** Leading icon; rendered in a tinted tile that follows `tone`. */
  icon?: ReactNode
  tone?: ConfirmDialogTone
  size?: ConfirmDialogSize
  pending?: boolean
  confirmDisabled?: boolean
  confirmLabel?: ReactNode
  /** Shown on the confirm button while `pending`. */
  pendingLabel?: ReactNode
  cancelLabel?: ReactNode
  onConfirm: () => void | Promise<void>
  /** Extra body content rendered under the description (previews, inputs, summaries). */
  children?: ReactNode
}

function ConfirmDialogHeader({
  icon,
  eyebrow,
  title,
  description,
  tone,
  children,
}: Pick<ConfirmDialogProps, "icon" | "eyebrow" | "title" | "description" | "children" | "tone">) {
  const destructive = tone === "destructive"
  return (
    <div className="flex items-start gap-3">
      {icon ? (
        <div
          className={cn(
            "grid size-9 shrink-0 place-items-center rounded-md",
            destructive ? "bg-destructive/10 text-destructive" : "bg-secondary text-foreground",
          )}
        >
          {icon}
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        {eyebrow ? (
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">{eyebrow}</div>
        ) : null}
        <AlertDialog.Title className={cn("text-base font-semibold", eyebrow && "mt-1")}>{title}</AlertDialog.Title>
        {description ? (
          <AlertDialog.Description className="mt-2 text-[13px] leading-5 text-muted-foreground">
            {description}
          </AlertDialog.Description>
        ) : null}
        {children}
      </div>
    </div>
  )
}

function ConfirmDialogFooter({
  cancelLabel,
  confirmLabel,
  pendingLabel,
  pending,
  confirmDisabled,
  tone,
  onConfirm,
}: Pick<
  ConfirmDialogProps,
  "cancelLabel" | "confirmLabel" | "pendingLabel" | "onConfirm" | "pending" | "confirmDisabled" | "tone"
>) {
  const destructive = tone === "destructive"
  return (
    <div className="mt-5 flex justify-end gap-2">
      <AlertDialog.Close
        disabled={pending}
        data-slot="confirm-dialog-cancel"
        render={<Button variant="ghost">{cancelLabel}</Button>}
      />
      <Button
        data-slot="confirm-dialog-confirm"
        onClick={() => void onConfirm()}
        disabled={pending || confirmDisabled}
        className={destructive ? "bg-destructive text-destructive-foreground hover:opacity-90" : undefined}
      >
        {pending ? pendingLabel : confirmLabel}
      </Button>
    </div>
  )
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  eyebrow,
  icon,
  tone = "neutral",
  size = "sm",
  pending = false,
  confirmDisabled = false,
  confirmLabel = "Confirm",
  pendingLabel = "Working…",
  cancelLabel = "Cancel",
  onConfirm,
  children,
}: ConfirmDialogProps) {
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className="fixed inset-0 z-50 bg-black/35 backdrop-blur-[2px] data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <AlertDialog.Viewport className="fixed inset-0 z-50 grid place-items-center p-4">
          <AlertDialog.Popup data-slot="confirm-dialog" className={cn(POPUP_CLASS, SIZE_CLASS[size])}>
            <ConfirmDialogHeader {...{ icon, eyebrow, title, description, tone, children }} />
            <ConfirmDialogFooter
              {...{ cancelLabel, confirmLabel, pendingLabel, pending, confirmDisabled, tone, onConfirm }}
            />
          </AlertDialog.Popup>
        </AlertDialog.Viewport>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
